import { useEffect, useRef } from 'react';
import { debounce } from 'lodash-es';
import type { PagesRefs } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.types';
import getElementPosition from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/utils/getElementPosition';
import { useHistory } from '@shared/core';
import { ACCOMMODATIONS_NOTE_TEXT_CLASS_NAME } from '@shared/constants/layout';

type KeepPageScrollPositionProps = {
  pagesRef: PagesRefs[];
  container: HTMLDivElement | null;
  targetPage?: HTMLDivElement | null;
};

const isCoupleNoteToggleMutation = (mutations: MutationRecord[]) => {
  return Array.from(mutations).some(mutation => {
    // Check if the mutation is a DOM element
    if (mutation.target instanceof HTMLElement && mutation.type === 'attributes' && mutation.attributeName === 'class') {
      const target = mutation.target;
      return target.className.includes(ACCOMMODATIONS_NOTE_TEXT_CLASS_NAME);
    }
    return false;
  });
};

export const useKeepPageScrollPosition = ({ pagesRef, container, targetPage }: KeepPageScrollPositionProps) => {
  const history = useHistory();

  // Keep pagesRef in a ref so the observer callback always reads the latest
  // array without the effect needing to re-run (and destroy the observer).
  const pagesRefRef = useRef(pagesRef);
  pagesRefRef.current = pagesRef;

  // Track page heights across observer callbacks in a ref that persists
  // across effect re-runs so height deltas are never lost.
  const heightMapRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!pagesRef?.length || !targetPage || !container) {
      return;
    }

    let observer: MutationObserver;

    const keepPageScrollPositionWhenDOMChanges = () => {
      // Track absolute-Y and scrollTop across callbacks so the correction
      // formula only compensates for *uncorrected* content growth.
      //
      // absoluteY = viewportTop + scrollTop is invariant under scroll — it
      // only changes when DOM content above the target grows or shrinks.
      // scrollTop changes from user scroll, ResizeObserver corrections, OR
      // our own prior adjustments.
      //
      // correction = contentShift − scrollChange gives the growth that hasn't
      // been compensated yet — so if another mechanism (the ResizeObserver in
      // useScrollToPageWhenUrlChanges) already scrolled to account for the
      // growth, scrollChange absorbs it and correction ≈ 0.
      let lastAbsoluteY = (targetPage?.getBoundingClientRect().top ?? 0) + (container?.scrollTop ?? 0);
      let lastScrollTop = container?.scrollTop ?? 0;

      // Seed the height map for any pages we haven't seen yet
      for (const { page, ref } of pagesRefRef.current) {
        if (ref && !heightMapRef.current.has(page.id)) {
          heightMapRef.current.set(page.id, ref.offsetHeight);
        }
      }

      const updatePageScroll = (mutationsList: MutationRecord[]) => {
        const currentPages = pagesRefRef.current;

        // --- Compute how much correction is still needed ---
        const currentAbsoluteY = (targetPage?.getBoundingClientRect().top ?? 0) + (container?.scrollTop ?? 0);
        const currentScrollTop = container?.scrollTop ?? 0;

        const contentShift = currentAbsoluteY - lastAbsoluteY;
        const scrollChange = currentScrollTop - lastScrollTop;
        const correction = contentShift - scrollChange;

        // --- Walk pages to update height map and check relevance ---
        let hasRelevantGrowthAbove = false;

        currentPages.forEach(({ ref: pageRef, page }) => {
          if (!pageRef) return;

          const trackedHeight = heightMapRef.current.get(page.id) ?? pageRef.offsetHeight;
          const newPageHeight = pageRef.offsetHeight;
          const heightDifference = newPageHeight - trackedHeight;

          // Always update tracked height
          heightMapRef.current.set(page.id, newPageHeight);

          if (heightDifference <= 0) return;

          // Skip accommodations only if triggered by More/Less button recently
          if (isCoupleNoteToggleMutation(mutationsList) && page.pageSlug === 'accommodations') {
            return;
          }

          const minContainerPositionY = container.scrollTop;
          const pagePosition = getElementPosition(pageRef, minContainerPositionY);
          const isPageBeforeVisibleArea = pagePosition.max <= minContainerPositionY;
          const isPagePartiallyVisibleArea = pagePosition.min <= minContainerPositionY && pagePosition.max >= minContainerPositionY;

          // Update the scroll position if the changed page is before the visible screen
          if (isPageBeforeVisibleArea || isPagePartiallyVisibleArea) {
            hasRelevantGrowthAbove = true;
          }
        });

        // Only adjust if there's uncorrected growth above the viewport.
        // A threshold of 1px avoids sub-pixel noise.
        if (hasRelevantGrowthAbove && correction > 1) {
          container.scrollTop = currentScrollTop + correction;
        }

        // Re-anchor for the next callback.
        lastAbsoluteY = (targetPage?.getBoundingClientRect().top ?? 0) + (container?.scrollTop ?? 0);
        lastScrollTop = container?.scrollTop ?? 0;
      };

      // Listen for container's DOM changes to update the page scroll position base on pages height changes
      observer = new MutationObserver(debounce(updatePageScroll, 20, { maxWait: 40 }));
      observer.observe(container, { attributes: true, childList: true, subtree: true });
    };

    keepPageScrollPositionWhenDOMChanges();

    return () => {
      observer.disconnect();
    };
    // container and targetPage control observer lifecycle.
    // pagesRef is read via ref inside the callback — not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, targetPage, history.location.pathname]);
};
